import React from "react";
import {
  withStyles,
  Divider,
  Grid,
  Button,
} from "@material-ui/core";
import { postData } from "../../../../../../api/componentAPIs";
import { withSnackbar } from "notistack";
import { compose } from "recompose";
import RouteMappingForm from "./RouteMappingForm";
import RouteMappingCardList from "./RouteMappingCardList";
import { ExpandLess } from "@material-ui/icons";
import Checkbox from "@material-ui/core/Checkbox";
import CollapsedSidePanel from "./CollapsedSidePanel";
import Events from "../../../../../../utilities/EventEmitter";
import { area, polygon, convertArea } from "@turf/turf";
import constants from "../constants";
import drawConstant from "../../../common/DrawTools/DrawConstant";
import { RMAXLogger } from "../../../../../../services/LoggerService/RMAXLogger";
import Tooltip from "../../../../../common/StyledToolTip";
import appConstants from "../../../../../../appConstants";
import Dropdown from "../../../../../common/Dropdown";
import {
  isExistingRouteNameByType,
  isExistingRouteName,
  getCloneTargetType,
  getExistingRouteNames,
  getNonClonableZoneIds,
  checkRouteMappingUpdated,
  getCloneIconTooltipText,
  getCloneAlertPopupMsg,
} from "../utils";
import CustomConfirmationDialog from "../../../../Dispatch/RouteExecution/DriverPopUp/common/CustomConfirmationDialog";
import CloneConfirmationContent from "./CloneConfirmationContent";
import AlertPopup from "./common/AlertPopup";
import { withAppConfig } from "../../../../../../appConfigContext";

const styles = (theme) => ({
  container: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  expandedContainer: {
    minWidth: 470,
    maxWidth: 470,
  },
  content: {
    flexGrow: 1,
    overflow: "auto",
  },
  divider: {
    backgroundColor: theme.AppBGColor,
  },
  head: {
    padding: "8px 24px",
    fontWeight: 600,
    fontSize: theme.headerTextFontSize,
    lineHeight: "16px",
    color: theme.HeaderSelectedText,
    display: "flex",
    justifyContent: "space-between",
    width: "90%",
  },
  dataContainer: {
    padding: "8px 24px",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.gridTextColor,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 100,
    width: "100%",
  },
  actionButton: {
    minWidth: "35%",
    marginLeft: 24,
    "&$disabledButton": {
      backgroundColor: theme.iconColor,
    },
  },
  disabledButton: {
    backgroundColor: theme.iconColor,
  },
  circularProgress: {
    color: theme.white,
  },
  flexRow: {
    display: "flex",
    direction: "column",
    "&>div": {
      flex: 1,
      width: "100%",
    },
  },
  fieldRoot: {},
  label: {
    color: theme.TextColor,
    transform: "translate(14px, 14px) scale(1)",
    fontSize: theme.headerTextFontSize,
    opacity: 0.7,
  },
  iconClass: {
    color: theme.iconColor,
    cursor: "pointer",
  },
  gridContainer: { margin: "0px", paddingTop: 8, width: "100%" },
  expandedButton: {
    justifyContent: "flex-start",
    width: "100%",
    color: theme.iconColor,
  },
  rotatedIcon: {
    transform: "rotate(90deg)",
  },
  sidePanelFooter: {
    background: theme.gridBGColor,
    height: "5%",
  },
  selectAllCheckbox: {
    padding: 0,
    color: theme.iconColor,
  },
  deleteOutlineIcon: {
    color: theme.ErrorMessageColor,
    padding: 0,
  },
  disableIcon: {
    cursor: "not-allowed",
    opacity: 0.7,
    padding: 0,
    color: theme.iconColor,
  },
  cardControlsContainer: {
    width: "100%",
  },
  dropDownContainer : {
    display : "flex",
    alignItems : "center"
  },
  cloneIcon : {
    marginLeft : 8,
    cursor : "pointer"
  },
  cloneIconDisabled : {
    color: theme.iconColor,
    opacity: 0.5,
    pointerEvents: "none",
  }
});

class RouteMappingSidePane extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      routeMappingInfo: null,
      routeMappingErrorInfo: this.getDefaultErrorInfo(),
      checkBoxSelections: [],
      openForm: false,
      allSelected: true,
      selectedFeatureIdsOnMap: [],
      updateInProgress : false,
      showCloneModal : false,
      clonableZoneIds : []
    };
    this.cardRef = React.createRef();
  }

  componentDidMount() {
    const { ROUTE_MAPPING_EVENTS } = drawConstant;
    Events.on(ROUTE_MAPPING_EVENTS.DRAWING_UPDATED, this.handleDrawingUpdate);
    Events.on(ROUTE_MAPPING_EVENTS.DRAWING_DELETED, this.handleDrawingDelete);
    Events.on(
      ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_MAP,
      this.handleDrawingSelectedOnMap
    );
    Events.on(ROUTE_MAPPING_EVENTS.CLOSE_FORM, this.closeForm);
  }

  componentWillUnmount() {
    const { ROUTE_MAPPING_EVENTS } = drawConstant;
    Events.off(ROUTE_MAPPING_EVENTS.DRAWING_UPDATED, this.handleDrawingUpdate);
    Events.off(ROUTE_MAPPING_EVENTS.DRAWING_DELETED, this.handleDrawingDelete);
    Events.off(
      ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_MAP,
      this.handleDrawingSelectedOnMap
    );
    Events.off(ROUTE_MAPPING_EVENTS.CLOSE_FORM, this.closeForm);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.data !== prevProps.data ||
      this.props.filteredRouteMappingList !== prevProps.filteredRouteMappingList
    ) {
      this.setState({
        checkBoxSelections: this.getDefaultCheckboxList(),
        allSelected:
          this.props.filteredRouteMappingList.length ===
          this.props.selectedIds.length,
      });
      this.checkSelection();
    }
    if (this.props.isAddUpdateMode !== prevProps.isAddUpdateMode) {
      this.setState({
        openForm: this.props.isAddUpdateMode,
      });
    }
  }

  checkSelection = () => {
    const featureIds = this.state.selectedFeatureIdsOnMap.filter((item) =>
      this.props.selectedIds.includes(item)
    );
    this.selectPolygonsOnMap(featureIds, false);
  };

  handleDrawingUpdate = (feature) => {
    const routeMappingInfo = this.state.routeMappingInfo
      ? {
          ...this.state.routeMappingInfo,
          boundary: feature?.geometry?.coordinates[0],
          types : [...this.state.routeMappingInfo.types],
          isFixedDeliveryZone : Boolean(this.state.routeMappingInfo?.isFixedDeliveryZone)
        }
      : this.getRouteMappingInfoById(feature.id, feature);
    this.setState({
      isExistingFeature: this.isExistingFeature(this.props.data, feature.id),
      openForm: true,
      routeMappingInfo: {
        ...routeMappingInfo,
      },
    });
    !this.props.isExpanded && this.props.toggleExpand();
  };

  handleDrawingDelete = (feature) => {
    const matchedItem = this.getExistingFeature(this.props.data, feature.id);
    if (matchedItem) {
      this.props.deleteCards([{ data: matchedItem }]);
    } else {
      this.closeForm();
    }
  };

  getExistingFeature = (existingData, featureId) => {
    return existingData.find((item) => item.routeMappingId === featureId);
  };

  getRouteMappingInfoById = (routeMappingId, feature) => {
    const matchedItem = this.getExistingFeature(
      this.props.data,
      routeMappingId
    );
    if (matchedItem) {
      return {
        routeMappingId: matchedItem.routeMappingId,
        featureId: matchedItem.primaryRouteName,
        primaryRoute: matchedItem.primaryRouteName,
        secondaryRoute: matchedItem.secondaryRouteName?.split(",") ?? [],
        selectedColor: matchedItem.color,
        boundary: feature?.geometry?.coordinates[0] ?? matchedItem.boundary,
        types: [matchedItem.type || appConstants.STOP_TYPE.Delivery],
        door : matchedItem?.door,
        isFixedDeliveryZone : Boolean(matchedItem?.isFixedDeliveryZone)
      };
    }
    return this.getDefaultEditInfo(feature);
  };

  getDefaultErrorInfo = () => {
    return {
      selectedColor: "",
      primaryRoute: "",
      secondaryRoute: "",
    };
  };

  getDefaultEditInfo = (feature) => {
    return {
      routeMappingId: null,
      featureId: feature ? feature.id : "",
      selectedColor: "",
      primaryRoute: "",
      secondaryRoute: [],
      boundary: feature ? feature.geometry.coordinates[0] : [],
      types: [appConstants.STOP_TYPE.Delivery],
      door : "",
      isFixedDeliveryZone : false
    };
  };

  isExistingFeature = (data, id) => {
    return Boolean(this.getExistingFeature(data, id)?.routeMappingId);
  };

  openForm = (id) => {
    this.selectPolygonsOnMap([id], true);
    const data = this.getRouteMappingInfoById(id);
    this.setState({
      openForm: true,
      isExistingFeature: this.isExistingFeature(this.props.data, id),
      routeMappingInfo: {
        ...data,
      },
    });
  };

  closeForm = (isDataSaved = false) => {
    const isExistingFeature = this.isExistingFeature(
      this.props.data,
      this.state.routeMappingInfo?.routeMappingId
    );
    let selectedFeatureIdsOnMap;
    if (!isDataSaved) {
      // delete new feature as Cancel button is clicked and feature not saved.
      Events.emit(
        drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_CREATED_ON_MAP,
        "cancel",
        isExistingFeature,
        this.state.routeMappingInfo?.featureId
      );
      selectedFeatureIdsOnMap = [];
    } else {
      selectedFeatureIdsOnMap = this.state.selectedFeatureIdsOnMap;
    }
    this.setState({
      openForm: false,
      routeMappingInfo: null,
      routeMappingErrorInfo: { ...this.getDefaultErrorInfo() },
      selectedFeatureIdsOnMap,
    });
  };

  handleChange = (name) => (selectedOption) => {
    let value;
    if (["primaryRoute","door"].includes(name)) {
      value = selectedOption?.value;
    } else if (name === "secondaryRoute") {
      value = selectedOption?.map((item) => item.value);
    } else {
      value = selectedOption;
    }
    this.setState((prevState) => ({
      routeMappingInfo: {
        ...prevState.routeMappingInfo,
        [name]: value,
      },
      routeMappingErrorInfo: {
        ...prevState.routeMappingErrorInfo,
        [name]: this.getFieldError(name, value),
      },
    }));
  };

  handleRouteTypeChange = (routeTypes) => {
      this.setState((prevState) => ({
        routeMappingInfo: {
          ...prevState.routeMappingInfo,
          types: routeTypes,
        },
        routeMappingErrorInfo: {
          ...prevState.routeMappingErrorInfo,
          ["primaryRoute"]: this.getPrimaryRouteError(
            this.state.routeMappingInfo.primaryRoute,
            routeTypes
          ),
          ["secondaryRoute"]: this.getSecondaryRouteError(
            this.state.routeMappingInfo.secondaryRoute,
            routeTypes
          ),
          types : this.getFieldError("types",routeTypes)
        },
      }));
  };

  getDefaultCheckboxList = () => {
    return this.props.filteredRouteMappingList.map((element) => ({
      name: element.primaryRouteName,
      isChecked: this.props.selectedIds
        ? this.props.selectedIds.includes(element.routeMappingId)
        : true,
      id: element.routeMappingId,
      data: element,
    }));
  };

  handleAllChecked = (event) => {
    const checkBoxOptions = this.state.checkBoxSelections.map((item) => ({
      ...item,
      isChecked: event.target.checked,
    }));
    const selectedFeatureIds = checkBoxOptions
      .filter((item) => item.isChecked)
      .map((item) => item.id);
    this.setState({
      checkBoxSelections: checkBoxOptions,
      allSelected: event.target.checked,
    });
    this.props.onSelectionChanged(selectedFeatureIds);
  };

  isAllCardSelected = () => {
    return (
      this.state.checkBoxSelections.filter((item) => item.isChecked).length ===
      this.props.data.length
    );
  };

  handleCheckBoxSelection = (event) => {
    //checkbox selections here indicate show/hide drawings filter.
    const checkBoxOptions = this.state.checkBoxSelections.map((item) => {
      return item.id == event.target.name
        ? {
            ...item,
            isChecked: event.target.checked,
          }
        : item;
    });
    const selectedFeatureIds = checkBoxOptions
      .filter((item) => item.isChecked)
      .map((item) => item.id);
    this.setState({
      checkBoxSelections: checkBoxOptions,
      allSelected: this.isAllCardSelected(),
    });
    this.props.onSelectionChanged(selectedFeatureIds);
  };

  handleDrawingSelectedOnMap = (featureIds) => {
    this.setState({
      selectedFeatureIdsOnMap: featureIds,
    });
    this.scrollToSelectedDrawingCard(featureIds[0]);
  };

  scrollToSelectedDrawingCard = (id) => {
    let matchedRef;
    this.cardRef?.current?.childNodes?.forEach((node) => {
      if (Number(node.id) === Number(id)) {
        matchedRef = node;
      }
    });
    matchedRef?.scrollIntoView({
      block: "start",
      behavior: "smooth",
    });
  };

  selectPolygonsOnMap = (selectedPolygonIds, isEditMode) => {
    Events.emit(
      drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_SIDE_PANEL,
      selectedPolygonIds,
      isEditMode
    );
  };

  computePolygonArea = (polygonCoordinates) => {
    const polygonGeoJson = polygon([polygonCoordinates]);
    return convertArea(area(polygonGeoJson), 'meters', 'miles');
  };

  populateSubmitDetails = () => {
    const {
      primaryRoute,
      secondaryRoute,
      selectedColor,
      boundary,
      routeMappingId,
      types,
      door,
      isFixedDeliveryZone
    } = this.state.routeMappingInfo;
    return {
      terminalId: this.props.userProfile.terminal.id,
      terminalCode: this.props.userProfile.terminal.code,
      routeMappingDetail: {
        routeMappingId: this.state.isExistingFeature ? routeMappingId : null,
        secondaryRouteName: secondaryRoute.join(",") || null,
        primaryRouteName: primaryRoute,
        color: selectedColor,
        zipCodes: null,
        boundary: JSON.stringify(boundary),
        area: this.computePolygonArea(boundary) || null,
        type : types,
        door,
        isFixedDeliveryZone: this.props.configJSON.enableFixedDeliveryZone ? isFixedDeliveryZone : null
      },
    };
  };

  handleSubmit = () => {
    const errorInfo = this.getErrorInfo();
    const error = Object.values(errorInfo).filter((value) => value).length;
    const isExistingFeature = this.state.isExistingFeature;
    const inputReqObj = this.populateSubmitDetails();
    if (!error) {
      this.setState({
        updateInProgress : true
      })
      postData(
        "saveRouteMapping",
        inputReqObj
      ).then(
        (response) => {
          if (response && response.status >= 200 && response.status < 400) {
            this.setState({
              updateInProgress : false
            })
            this.addTracking(
              isExistingFeature,
              inputReqObj
            );
            Events.emit(
              drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_CREATED_ON_MAP,
              "save",
              isExistingFeature,
              this.state.routeMappingInfo.routeMappingId
            );
            this.props.storeCreatedFeature(response.data.routeMappingIds);
            this.closeForm(true);
            this.props.enqueueSnackbar(
              "Route mapping area saved successfully ",
              {
                variant: "success",
              }
            );
            this.props.getRouteMappingData();
            return;
          }
          this.handleSaveRouteMappingError(response.status);
        },
        () => {
          this.handleSaveRouteMappingError();
          this.closeForm(false);
        }
      );
    }
  };

  handleSaveRouteMappingError = (statusCode) => {
    this.state.updateInProgress &&
      this.setState({
        updateInProgress: false,
      });
    if (statusCode === 403) {
      return;
    }
    this.props.enqueueSnackbar("Error while saving route mapping area", {
      variant: "error",
    });
  };

  addTracking = (isExistingFeature, request) => {
    if (isExistingFeature) {
      RMAXLogger.info("RouteMappingUpdated", {
        routeMappingData: request.routeMappingDetail,
      });
    } else {
      RMAXLogger.info("RouteMappingCreated", {
        routeMappingData: request.routeMappingDetail,
      });
    }
  };

  getErrorInfo = () => {
    let routeMappingErrorInfo = {};
    Object.keys(this.state.routeMappingInfo).forEach((fieldName) => {
      routeMappingErrorInfo[fieldName] = this.getFieldError(
        fieldName,
        this.state.routeMappingInfo[fieldName]
      );
    });
    this.setState({
      routeMappingErrorInfo,
    });
    return routeMappingErrorInfo;
  };

  getFieldError = (fieldName, value) => {
    switch (fieldName) {
      case "primaryRoute":
        return this.getPrimaryRouteError(
          value,
          this.state.routeMappingInfo.types
        );
      case "selectedColor":
        return value && value.trim() ? null : "* Color is required";
      case "secondaryRoute":
        return this.getSecondaryRouteError(
          value,
          this.state.routeMappingInfo.types
        );
      case "types":
        return value.length ? null : "* Route Type is required";
      case "door":
        return this.getDoorError(value);
      default:
        return null;
    }
  };

  getDoorError = (value) => {
    if(!this.props.showDoorNumber){
      return null;
    }
    return value?.trim() ? null : "* Door # is required";
  }

  getPrimaryRouteErrorForExistingFeature = (routeNames,routeTypes) => {
    const { routeMappingInfo } = this.state;
    const originalRouteMapping = this.getRouteMappingInfoById(routeMappingInfo.routeMappingId)
    const isRouteMappingUpdated = checkRouteMappingUpdated({
      originalRouteNames : [originalRouteMapping.primaryRoute],
      routeNames,
      routeTypes,
      originalRouteTypes : originalRouteMapping.types
    })
    if (!isRouteMappingUpdated) {
      return null;
    }
    if (
      isRouteMappingUpdated &&
      isExistingRouteName({
        routeMappingData: this.props.data,
        routeNames,
        routeType: routeTypes[0],
      })
    ) {
      return `* Primary route name must be unique within ${routeTypes[0].toLowerCase()} route mapping areas`;
    }
    return null;
  }

  getPrimaryRouteErrorForNewFeature = (routeNames,routeTypes) => {
    const { STOP_TYPE } = appConstants;
    if (
      isExistingRouteNameByType({
        routeMappingData: this.props.data,
        routeNames,
        routeType: STOP_TYPE.Delivery,
        routeTypes,
      })
    ) {
      return `* Primary route name must be unique within delivery route mapping areas`;
    }
    if (
      isExistingRouteNameByType({
        routeMappingData: this.props.data,
        routeNames,
        routeType: STOP_TYPE.Pickup,
        routeTypes,
      })
    ) {
      return `* Primary route name must be unique within pickup route mapping areas`;
    }
    return null;
  }

  getPrimaryRouteError = (value, routeTypes) => {
    if (!(value && value.trim())) {
      return "* Primary route name is required";
    }
    if(this.state.isExistingFeature){
      return this.getPrimaryRouteErrorForExistingFeature([value],routeTypes)
    }
    return this.getPrimaryRouteErrorForNewFeature([value], routeTypes);
  };

  getSecondaryRouteErrorForExistingFeature = (routeNames,routeTypes) => {
    const { routeMappingInfo } = this.state;
    const originalRouteMapping = this.getRouteMappingInfoById(routeMappingInfo.routeMappingId)
    const isRouteMappingUpdated = checkRouteMappingUpdated({
      originalRouteNames: originalRouteMapping.secondaryRoute,
      routeNames,
      routeTypes,
      originalRouteTypes: originalRouteMapping.types,
    });
    if (!isRouteMappingUpdated) {
      return null;
    }
    const secondaryRouteNamesToValidate = routeNames.filter(
      (routeName) => !originalRouteMapping.secondaryRoute.includes(routeName)
    );
    if (
      isRouteMappingUpdated &&
      isExistingRouteName({
        routeMappingData: this.props.data,
        routeNames: secondaryRouteNamesToValidate,
        routeType: routeTypes[0],
      })
    ) {
      return `* Secondary route name must be unique within ${routeTypes[0].toLowerCase()} route mapping areas`;
    }
    return null;
  }

  getSecondaryRouteErrorForNewFeature = (routeNames,routeTypes) => {
    const { STOP_TYPE } = appConstants;
    if (
      isExistingRouteNameByType({
        routeMappingData: this.props.data,
        routeNames,
        routeType: STOP_TYPE.Delivery,
        routeTypes,
      })
    ) {
      return `* Secondary route name must be unique within delivery route mapping areas`;
    }
    if (
      isExistingRouteNameByType({
        routeMappingData: this.props.data,
        routeNames,
        routeType: STOP_TYPE.Pickup,
        routeTypes,
      })
    ) {
      return `* Secondary route name must be unique within pickup route mapping areas`;
    }
    return null;
  }

  getSecondaryRouteError = (value, routeTypes) => {
    const { routeMappingInfo } = this.state;
    const currentPR = routeMappingInfo.primaryRoute;
    if (value.includes(currentPR)) {
      return "* Secondary route name should not match with primary route name";
    }
    if(this.state.isExistingFeature){
      return this.getSecondaryRouteErrorForExistingFeature(value,routeTypes)
    }
    return this.getSecondaryRouteErrorForNewFeature(value, routeTypes);
  };

  cloneSelectedFeatures = () => {
    const targetCloneType = getCloneTargetType(
      this.props.selectedFilterOption.value
    );
    const existingRouteNamesList = getExistingRouteNames(
      this.props.data,
      targetCloneType
    );
    const clonableZoneIds = this.props.selectedIds.filter((id) => {
      const routeMappingData = this.getRouteMappingInfoById(id);
      const routes = [
        routeMappingData.primaryRoute,
        ...routeMappingData.secondaryRoute,
      ];
      return !routes.some((route) => existingRouteNamesList.includes(route));
    });
    this.setState({
      clonableZoneIds,
      showCloneModal: true,
    });
  }

  
  cancelClone = () => {
    this.setState({
      showCloneModal: false,
      clonableZoneIds : []
    });
  }

  cloneZones = () => {
    const targetType = getCloneTargetType(
      this.props.selectedFilterOption.value
    );
    const inputObj = {
      terminalId: this.props.userProfile.terminal.id,
      targetType,
      routeMappingIds: this.state.clonableZoneIds,
    };
    postData("CopyRouteMapping",inputObj).then(response=>{
      if(response?.status >= 200 && response?.status < 400){
        this.setState({
          showCloneModal : false,
          clonableZoneIds : []
        })
        this.props.enqueueSnackbar(
          "Route mapping area copied successfully ",
          {
            variant: "success",
          }
        );
        this.props.getRouteMappingData();
        return;
      }
      this.handleCloneZonesError(response.status);
    },()=>{
      this.handleCloneZonesError();
    })
  }

  handleCloneZonesError = (statusCode) => {
    this.setState({
      showCloneModal: false,
      clonableZoneIds: [],
      updateInProgress: false,
    });
    if (statusCode === 403) {
      return;
    }
    this.props.enqueueSnackbar('Error while copying zones', { variant: "error" });
  };

  render() {
    const { classes, selectedFilterOption } = this.props;
    const {
      routeMappingInfo,
      openForm,
      allSelected,
      updateInProgress,
      routeMappingErrorInfo,
      isExistingFeature,
      selectedFeatureIdsOnMap,
      checkBoxSelections,
      clonableZoneIds,
    } = this.state;
    const { STOP_TYPE } = appConstants;
    const currentType = selectedFilterOption.value;
    const showCloneButton = [STOP_TYPE.Delivery, STOP_TYPE.Pickup].includes(
      currentType
    );
    const enableClone = this.props.selectedIds.length > 0;
    return (
      <>
        {!this.props.isExpanded && (
          <CollapsedSidePanel toggleExpand={this.props.toggleExpand} />
        )}

        {this.props.isExpanded && (
          <div
            id="sidePanel_expanded"
            className={`${classes.container} ${classes.expandedContainer}`}
          >
            <div>
              <Grid item xs={12} className={classes.head}>
                Route Mapping Details
              </Grid>
              <Divider className={classes.divider} />
            </div>
            <div className={classes.cardControlsContainer}>
              {!openForm && (
                <>
                  <div className={classes.head}>
                    <Tooltip
                      title={
                        allSelected
                          ? "Hide all route mapping area on map"
                          : "Show all route mapping area on map"
                      }
                    >
                      <Checkbox
                        color="primary"
                        className={classes.selectAllCheckbox}
                        onChange={this.handleAllChecked}
                        checked={allSelected}
                      />
                    </Tooltip>
                    <div className={classes.dropDownContainer}>
                      <Dropdown
                        autoFocus={false}
                        value={selectedFilterOption.label}
                        options={constants.ROUTE_MAPPING_FILTER_OPTIONS}
                        onChange={this.props.routeMappingFilterChange}
                        isClearable={false}
                      />
                      {showCloneButton && (
                        <Tooltip
                          title={getCloneIconTooltipText({
                            currentType,
                            selectedIds: this.props.selectedIds,
                          })}
                        >
                          <div
                            onClick={
                              enableClone ? this.cloneSelectedFeatures : ""
                            }
                          >
                            <i
                              className={`icon-copy ${classes.cloneIcon} ${
                                enableClone ? "" : classes.cloneIconDisabled
                              }`}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <Divider className={classes.divider} />

            <div className={`${classes.content} customScroll`}>
              <div className={classes.dataContainer}>
                <Grid container spacing={24} className={classes.gridContainer}>
                  {openForm ? (
                    <RouteMappingForm
                      handleChange={this.handleChange}
                      handleRouteTypeChange={this.handleRouteTypeChange}
                      onClose={this.closeForm}
                      updateInProgress={updateInProgress}
                      onSubmit={this.handleSubmit}
                      errorInfo={routeMappingErrorInfo}
                      editInfo={routeMappingInfo}
                      isExistingFeature={isExistingFeature}
                      originalRouteMapping={this.getRouteMappingInfoById(
                        routeMappingInfo.routeMappingId
                      )}
                      doorData={this.props.doorData}
                      showDoorNumber={this.props.showDoorNumber}
                    />
                  ) : (
                    <RouteMappingCardList
                      selectedFeatureIdsOnMap={selectedFeatureIdsOnMap}
                      checkBoxList={checkBoxSelections}
                      onChange={this.handleCheckBoxSelection}
                      openForm={this.openForm}
                      selectedFeatureIds={this.props.selectedIds}
                      forwardRef={this.cardRef}
                      selectPolygonsOnMap={this.selectPolygonsOnMap}
                      showDoorNumber={this.props.showDoorNumber}
                    />
                  )}
                </Grid>
              </div>
            </div>
            <Divider className={classes.divider} />
            <div id="sidePanelToggle" className={classes.sidePanelFooter}>
              <Button
                classes={{ root: classes.expandedButton }}
                onClick={this.props.toggleExpand}
              >
                Hide Details <ExpandLess className={classes.rotatedIcon} />
              </Button>
            </div>
          </div>
        )}
        {this.state.showCloneModal && clonableZoneIds.length > 0 && (
          <CustomConfirmationDialog
            title="Copy Zones"
            cancelButtonLabel="Cancel"
            confirmButtonLabel="Confirm"
            onConfirm={this.cloneZones}
            onCancel={this.cancelClone}
          >
            <CloneConfirmationContent
              currentType={currentType}
              nonClonableZoneIds={getNonClonableZoneIds(
                this.props.selectedIds,
                clonableZoneIds
              )}
              routeMappingData={this.props.data}
            />
          </CustomConfirmationDialog>
        )}
        {this.state.showCloneModal && !clonableZoneIds.length && (
          <AlertPopup
            title="Copy Zones"
            message={getCloneAlertPopupMsg(currentType)}
            onClose={this.cancelClone}
          />
        )}
      </>
    );
  }
}

export default compose(
  withStyles(styles),
  withSnackbar, 
  withAppConfig
)(RouteMappingSidePane);
